import React, { useState, useEffect } from 'react';
import '../../styles/components/SchedulerTasks.scss'
import { Button, Box, Tooltip, Typography, IconButton, Grid, Card, CardContent, Fab } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Alarm as AlarmIcon,
  Pause as PauseIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import moment from "moment";
import cronstrue from 'cronstrue';
import customLocaleText from '../../constants/DataGridLocaleTextConstants.js';
import 'cronstrue/locales/es.js';
import { useLoader } from '../../context/LoaderContext.js';
import {
  getAppSchedulerJobsByDomainId,
  createAppSchedulerJob,
  updateAppSchedulerJobsAttr,
  executeAppSchedulerJobsByName,
  deleteAppSchedulerById,
  toggleAppSchedulerJobState
} from '../../api/schedulerJobsApi.js';
import { getAppTemplateDomainById } from '../../api/appTemplatesApi.js';
import { useDomain } from '../../context/DomainContext.js';
import SchedulerTasksModal from './SchedulerTasksModal.js';
import DeleteModal from '../DeleteModal.js';
import { useAuth } from '../../context/AuthContext.js';

moment.locale("es");

const SchedulerTasks = () => {
  const [paginationModel] = useState({ page: 0, pageSize: 5 })
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, text: '', error: false });
  const { showLoader, hideLoader } = useLoader();
  const { selectedDomain } = useDomain();
  const { user } = useAuth();

  const handleCloseSnackbar = (reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar({ open: false, text: '', error: false });
  };

  const loadTemplates = () => {
    showLoader();
    getAppTemplateDomainById(selectedDomain)
      .then(response => {
        setTemplates(response.data)
      })
      .finally(() => { hideLoader() })
      .catch(error => console.error('Error fetching templates:', error));
  };

  const loadScheduleJobs = () => {
    showLoader();
    getAppSchedulerJobsByDomainId(selectedDomain)
      .then(response => {
        setTasks(response.data)
      })
      .finally(() => { hideLoader() })
      .catch(error => console.error('Error fetching Scheduler Jobs:', error));
  };

  const handlePlay = async (name) => {
    try {
      showLoader();
      const response = await executeAppSchedulerJobsByName(name);
      if (response.data?.status === 'success') {
        setOpenSnackbar({ open: true, text: `Tarea ${name} ejecutada con éxito`, error: false });
        loadScheduleJobs();
        setTimeout(() => {
          loadScheduleJobs();
        }, 5000);
      } else {
        throw new Error(response.data?.message || "Error ejecutando acción");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message || "Error ejecutando acción";
      setOpenSnackbar({ open: true, text: errorMsg, error: "error" });
    } finally {
      hideLoader();
    }
  };

  const handleToggleState = async (name, currentState) => {
    try {
      showLoader();
      const nextState = currentState === 'ENABLED' ? 'PAUSED' : 'ENABLED';
      await toggleAppSchedulerJobState(name, { name, state: nextState });
      setOpenSnackbar({
        open: true,
        text: `Tarea ${nextState === 'ENABLED' ? 'habilitada' : 'deshabilitada'} con éxito`,
        error: false
      });
      loadScheduleJobs();
    } catch (error) {
      setOpenSnackbar({
        open: true,
        text: `Error al cambiar el estado: ${error.message || error}`,
        error: true
      });
    } finally {
      hideLoader();
    }
  };

  const handleSave = async (data, isNew) => {
    try {
      showLoader();
      let keywordMessage = isNew ? 'La tarea se creó correctamente' : 'La tarea se guardó correctamente';
      isNew ? await createAppSchedulerJob(data) : await updateAppSchedulerJobsAttr(selectedSchedule.id, data);

      setIsModalOpen(false);
      setSelectedSchedule(null);
      setOpenSnackbar({ open: true, text: keywordMessage, error: false });
      loadScheduleJobs();
    } catch (error) {
      if (error.type === 'duplicate') {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      } else {
        setOpenSnackbar({ open: true, text: `Error: ${error.message}`, error: true });
      }
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (selectedDomain) {
      loadScheduleJobs();
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain]);

  const handleOpenModal = (schedule) => {
    setSelectedSchedule(schedule);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const getTemplateNameById = (param) => {
    const templateId = param?.body?.signature_template_id || ''
    if (!templateId) return '';
    if (templateId === 'google-workspace-sync') {
      return '🔄 Sincronización Google Workspace';
    }
    const templateName = templates.find(t => t.id === templateId)
    return templateName?.template_name || '-';
  }

  const handleOpenDeleteModal = (schedule) => {
    setSelectedSchedule(schedule);
    setOpenDeleteModal(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenDeleteModal(false);
    setSelectedSchedule(null);
  };

  const handleDelete = async (id) => {
    try {
      showLoader();
      await deleteAppSchedulerById(id);
      setOpenDeleteModal(false);
      setSelectedSchedule(null);
      setOpenSnackbar({ open: true, text: 'La tarea se eliminó correctamente', error: false });
      loadScheduleJobs();
      loadTemplates();
    } catch (error) {
      setOpenSnackbar({ open: true, text: 'Hubo un error al eliminar la tarea', error: true });
    } finally {
      hideLoader();
    }
  };

  const getSchedulerName = (name) => {
    return name.match(/jobs\/([^/]+)$/)?.[1]
  }

  const columns = [
    {
      field: "name",
      headerName: "Nombre",
      flex: 1,
      valueGetter: (params) => getSchedulerName(params) || "",
    },
    { field: "description", headerName: "Descripción", flex: 1 },
    { field: "httpTarget", headerName: "Template / Tipo de Tarea", flex: 1, valueGetter: (param) => getTemplateNameById(param) },
    {
      field: "status",
      headerName: "Estado última ejecución",
      flex: 1,
      renderCell: (params) => {
        const hasRun = !!params.row.lastAttemptTime;
        if (!hasRun) {
          return (
            <Box display="flex" alignItems="center" sx={{ color: 'text.secondary' }}>
              <CheckCircleIcon color="disabled" sx={{ marginRight: 1 }} />
              Aún no se ha ejecutado
            </Box>
          );
        }
        const status = params.row.status;
        const isSuccess = !status || status.code === 0 || (status.code <= 0 && (!status.message || status.message.trim() === ''));
        return (
          <Box display="flex" alignItems="center">
            {isSuccess ? (
              <CheckCircleIcon color="success" sx={{ marginRight: 1 }} />
            ) : (
              <CancelIcon color="error" sx={{ marginRight: 1 }} />
            )}
            {isSuccess ? "Sin errores" : "Error"}
          </Box>
        );
      },
    },
    {
      field: "state",
      headerName: "Estado",
      flex: 1,
      valueGetter: (params) => (params === "ENABLED" ? "Habilitado" : "No Habilitado"),
    },
    {
      field: "schedule",
      headerName: "Frecuencia",
      flex: 1,
      valueGetter: (params) => (cronstrue.toString(params, { locale: "es" }))
    },
    {
      field: "lastAttemptTime",
      headerName: "Última ejecución",
      flex: 1,
      valueGetter: (params) => params?.seconds ? moment.unix(params.seconds).format("DD MMM YYYY HH:mm:ss") : '-',
    },

  ];

  if (user?.isAdmin) {
    columns.push({
      display: "flex",
      field: "actions",
      headerName: "Acciones",
      flex: 1.2,
      renderCell: (params) => {
        const isEnabled = params.row.state === 'ENABLED';
        return (
          <Box display="flex" gap={1}>
            <Tooltip title="Ejecutar">
              <IconButton onClick={() => handlePlay(getSchedulerName(params.row.name))}>
                <PlayArrowIcon color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isEnabled ? "Deshabilitar" : "Habilitar"}>
              <IconButton onClick={() => handleToggleState(getSchedulerName(params.row.name), params.row.state)}>
                {isEnabled ? <PauseIcon color="warning" /> : <PlayCircleOutlineIcon color="success" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton onClick={() => handleOpenModal(params.row)}>
                <EditIcon color="action" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton
                onClick={() => handleOpenDeleteModal(params.row)}
                aria-label="delete"
                data-testid="DeleteIcon"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    });
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 234px)',
      pt: 3,
      pb: 1
    }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon'>
                <AlarmIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Total
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {tasks.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent className='resume-container'>
              <Fab color="primary" aria-label="add" className='resume-icon active-templates'>
                <AlarmIcon />
              </Fab>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom className='resume-title'>
                Tareas activas
              </Typography>
              <Typography variant="h5" component="div" className='resume-value'>
                {Array.isArray(tasks) ? tasks?.filter(u => u.state === "ENABLED").length : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {user?.isAdmin && <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpenModal}
        sx={{ marginBottom: 2, width: "fit-content" }}
      >
        Nueva Tarea
      </Button>}
      <DataGrid
        rows={tasks}
        columns={columns}
        initialState={{ pagination: { paginationModel } }}
        pageSizeOptions={[5, 10, 15, 20]}
        localeText={customLocaleText}
        getRowId={(row) => row.name}
        checkboxSelection={false}
      />

      <DeleteModal
        openConfirmModal={openDeleteModal}
        selectedItem={{ ...selectedSchedule, id: selectedSchedule?.name ? (selectedSchedule.name) : null }}
        handleCloseConfirmModal={handleCloseConfirmModal}
        onDelete={handleDelete}
        message={'Esta seguro de eliminar la tarea: ' + (selectedSchedule?.name ? getSchedulerName(selectedSchedule?.name) : '')}
      />
      <SchedulerTasksModal
        selectedSchedule={selectedSchedule}
        handleCloseModal={handleCloseModal}
        openModal={isModalOpen}
        templates={templates}
        handleSave={handleSave}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        open={openSnackbar.open}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={openSnackbar.error ? 'error' : 'success'}
          variant="filled"
        >
          {openSnackbar.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchedulerTasks;
